import {defencePlan} from './defencePlan';
import type { Spot, Slots, Spots, LegacyPhase, System, FormationInput } from "./types.js";
/**
 * Where each player actually stands, for each phase of a rotation.
 *
 * Three phases, in the order a rally presents them:
 *
 *   base    the strict rotational slot — the centre of your zone. What the
 *           rulebook alone says about where you belong.
 *   serve   the moment of the service hit: the practical shape a team takes
 *           while still satisfying Rule 7.4 (three passers, setter tucked in,
 *           net players up), or the serving team's free base.
 *   switch  after contact, when Rule 7.4.4 frees everyone and players run to
 *           the roles they actually play: setter right, outsides left, middle
 *           middle.
 *
 * Tactical wishes are expressed as *desired* spots and then projected onto the
 * legal set by `enforceRules`, so a receiving formation cannot be illegal by
 * construction. Passers are anchored — they hold the depth the ball demands and
 * everybody else yields around them, which is what real teams do and what makes
 * the setter's sprint to the net necessary.
 */

import { COURT, zoneAnchor } from "./court.js";
import { rosterFor } from "./libero.js";
import { isFrontRow } from "./rotation.js";

/** Frozen original phases; defence is an additional application formation. */
export const PHASES: LegacyPhase[] = ["base", "serve", "switch"];

/**
 * The seam for other systems. The resolvers read the system's roles rather than
 * hard-coding them, so a 6-2 becomes a second descriptor rather than a rewrite.
 */
export const SYSTEM_5_1: System = {
  id: "5-1",
  setter: "S",
  /**
   * The three who take the serve: both outsides, plus the libero — or, for a
   * team playing without one, whichever middle is in the back row that
   * rotation, which is the player the libero would have replaced.
   */
  passersFor(slots) {
    const third = slots.has("L")
      ? "L"
      : ["MB1", "MB2"].find((role) => !isFrontRow(slots.get(role)!));
    return ["OH1", "OH2", third].filter((role): role is string => Boolean(role && slots.has(role)));
  },
};

/** Minimum separation the rules projection leaves between neighbours, metres. */
const MARGIN = 0.4;

/** Minimum distance between any two players so nobody stands inside anybody. */
const MIN_GAP = 0.95;

/** Lateral lanes for the three passers, left to right. */
const PASSING_LANES = [-2.6, 0, 2.6];

/** How deep a passer stands, depending on the row they are rotationally in. */
const PASSER_DEPTH = { front: 5.9, back: 6.6 };

/** Net players stand at the lateral centre of their front-row zone. */
const NET_X: Record<number, number> = { 4: -2.9, 3: 0, 2: 2.9 };

/** A back-row player who is not passing hides toward their sideline. */
const HIDDEN_X: Record<number, number> = { 5: -3.7, 6: 0, 1: 3.7 };

/**
 * Where the opposite stands in serve receive.
 *
 * She never passes. Instead she plays deep cover behind the three passers and
 * takes everything that gets past the passing line, so she stands deeper than
 * any of them and offset toward the right of her own zone: outside the passer
 * when she is in zone 1, between 6 and 1 when she is in zone 6.
 */
const DEEP_COVER: Record<number, Spot> = {
  1: { x: 3.9, z: 7.6 }, // out to the right of the zone 1 passer
  6: { x: 1.6, z: 7.6 }, // between 6 and 1
  5: { x: -1.6, z: 7.6 }, // between 5 and 6, the same shape mirrored
};

const NET_DEPTH = 1.0;
const SETTER_NET_DEPTH = 1.6;
const SERVICE_DEPTH = COURT.halfDepth + 1.2;

/** The front-row zone each back-row zone is measured against (Rule 7.4.3.1). */
const FRONT_PARTNER: Record<number, number> = { 5: 4, 6: 3, 1: 2 };
const FRONT_BACK_PAIRS = [
  [4, 5],
  [3, 6],
  [2, 1],
];
const LATERAL_PAIRS = [
  [4, 3],
  [3, 2],
  [5, 6],
  [6, 1],
];

/* ------------------------------------------------------------------ phases */

function basePhase(slots: Slots) {
  const spots: Spots = new Map();
  for (const [role, zone] of slots) spots.set(role, zoneAnchor(zone));
  return spots;
}

function receivePhase(slots: Slots, system: System, rotation: number) {
  const zoneByRole = slots;
  const passers = system.passersFor(slots);

  // Assign passing lanes left to right, following the passers' rotational
  // order across the court so nobody has to cross a teammate to get there.
  const laneOrder = [...passers].sort((a, b) => {
    const ax = zoneAnchor(zoneByRole.get(a)!).x;
    const bx = zoneAnchor(zoneByRole.get(b)!).x;
    if (ax !== bx) return ax - bx;
    // Tie: the front-row passer takes the outer lane, since they start nearer
    // the net and have less ground to give up.
    return Number(isFrontRow(zoneByRole.get(b)!)) - Number(isFrontRow(zoneByRole.get(a)!));
  });

  const spots: Spots = new Map();
  const anchored = new Set<number>();

  laneOrder.forEach((role, index) => {
    const zone = zoneByRole.get(role)!;
    const front = isFrontRow(zone);
    spots.set(role, {
      x: PASSING_LANES[index],
      z: front ? PASSER_DEPTH.front : PASSER_DEPTH.back,
    });
    anchored.add(zone);
  });

  for (const [role, zone] of zoneByRole) {
    if (spots.has(role)) continue;

    if (isFrontRow(zone)) {
      spots.set(role, {
        x: NET_X[zone],
        z: role === system.setter ? SETTER_NET_DEPTH : NET_DEPTH,
      });
      continue;
    }

    if (role === system.setter) {
      // The setter wants the right sideline and the net. Whether they can have
      // the net depends on whether the player in front of them is passing.
      const partnerPassing = anchored.has(FRONT_PARTNER[zone]);
      spots.set(role, {
        x: 3.6,
        z: partnerPassing ? PASSER_DEPTH.back : SETTER_NET_DEPTH + MARGIN,
      });
      continue;
    }

    // Everyone else in the back row is the opposite, playing deep cover.
    spots.set(role, { ...DEEP_COVER[zone] });
  }

  // Rotation 1: opposite waits on the attack line toward the left sideline.
  if (rotation === 1 && system.id === "5-1" && slots.has("OPP")) {
    spots.set("OPP", { x: -3.4, z: COURT.attackLine });
  }

  // Rotation 2: the zone-6 setter stacks behind the opposite near the attack
  // line rather than under the zone-1 passer, keeping clear lateral margins to
  // both back-row neighbours (Rule 7.4.3.2) while staying behind zone 3.
  if (rotation === 2 && system.id === "5-1") {
    spots.set(system.setter, { x: 1.2, z: 2.8 });
  }

  // Rotation 3: the zone-5 setter stands beside the middle in zone 4 rather
  // than under the zone-6 passer, so "left of zone 6" is visibly satisfied
  // while she stays behind zone 4 (Rule 7.4.3).
  if (rotation === 3 && system.id === "5-1") {
    spots.set(system.setter, { x: -2.0, z: SETTER_NET_DEPTH + MARGIN });
  }

  // Rotation 5: the front-row setter in zone 3 shades toward her setting spot
  // between zones 3 and 2, staying left of the zone-2 middle (Rule 7.4.3.2);
  // the middle waits on the attack line so she can approach after the pass,
  // still nearer the net than the zone-1 passer (Rule 7.4.3.1).
  if (rotation === 5 && system.id === "5-1") {
    spots.set(system.setter, { x: 1.6, z: SETTER_NET_DEPTH });
    spots.set("MB2", { x: 3.4, z: COURT.attackLine });
  }

  // Rotation 4: the front-row outside in zone 2 passes from the left lane so
  // her transition to attack from zone 4 is short; the back-row outside takes
  // the middle lane and the zone-6 passer the right lane. The setter and
  // middle stack on the left at the net so zone 2 stays right of zone 3 and
  // zone 3 right of zone 4 at contact (Rule 7.4.3.2).
  if (rotation === 4 && system.id === "5-1") {
    const zone6 = [...zoneByRole].find(([, zone]) => zone === 6)?.[0];
    spots.set("OH2", { x: PASSING_LANES[0], z: PASSER_DEPTH.front });
    spots.set("OH1", { x: PASSING_LANES[1], z: PASSER_DEPTH.back });
    if (zone6) spots.set(zone6, { x: PASSING_LANES[2], z: PASSER_DEPTH.back });
    spots.set(system.setter, { x: -4.1, z: SETTER_NET_DEPTH });
    spots.set("MB2", { x: -3.1, z: NET_DEPTH });
  }

  enforceRules(spots, zoneByRole, anchored);
  return spots;
}

function servePhase(slots: Slots, _system: System, server: string | null) {
  const spots: Spots = new Map();

  for (const [role, zone] of slots) {
    if (role === server) {
      spots.set(role, { x: 2.0, z: SERVICE_DEPTH });
    } else if (isFrontRow(zone)) {
      spots.set(role, { x: NET_X[zone], z: NET_DEPTH });
    } else {
      spots.set(role, { x: HIDDEN_X[zone] * 0.85, z: 6.4 });
    }
  }

  // The serving team is free of Rule 7.4, so nothing is projected here — but
  // players still may not occupy the same patch of floor.
  separate(spots, new Set([server]));
  return spots;
}

function switchPhase(slots: Slots, system: System) {
  const spots: Spots = new Map();
  const frontRow = [...slots].filter(([, zone]) => isFrontRow(zone));

  const roleAt = (predicate: (role: string) => boolean) => frontRow.find(([role]) => predicate(role))?.[0];
  const frontOutside = roleAt((role) => role.startsWith("OH"));
  const frontMiddle = roleAt((role) => role.startsWith("MB"));
  const frontRight = frontRow.find(
    ([role]) => role !== frontOutside && role !== frontMiddle,
  )?.[0];

  if (frontOutside) spots.set(frontOutside, { x: -2.9, z: 1.2 });
  if (frontMiddle) spots.set(frontMiddle, { x: 0, z: 1.0 });
  if (frontRight) spots.set(frontRight, { x: 2.9, z: 1.2 });

  /**
   * Back-row defence.
   *
   * The outside hitter takes the middle of the back court and the libero takes
   * the left, not the other way round: the libero digs the line, and the
   * outside is already travelling that way to hit the next ball.
   *
   * A middle only appears back here when there is no libero to replace her —
   * either the team plays without one, or she is the one serving, since a
   * libero may not serve. Having just served from zone 1, she defends there
   * rather than crossing the court, and whoever else is back row covers the
   * side the libero has left empty.
   */
  const DEFENCE = {
    middle: { x: 0.2, z: 7.0 },
    left: { x: -3.4, z: 6.6 },
    right: { x: 3.0, z: 6.6 },
  };

  const claimed = new Set();
  const claim = (role: string, ...preferences: (keyof typeof DEFENCE)[]) => {
    const spot = preferences.find((side) => !claimed.has(side)) ?? "middle";
    claimed.add(spot);
    spots.set(role, { ...DEFENCE[spot] });
  };

  const backRow = [...slots].filter(([role]) => !spots.has(role));

  // In order of how fixed each job is, so the flexible players take what is left.
  for (const [role] of backRow) {
    if (role === system.setter) {
      // A back-row setter is not a defender: she goes up to set.
      spots.set(role, { x: 2.2, z: 2.4 });
    }
  }
  for (const [role] of backRow) {
    if (role.startsWith("OH") && !spots.has(role)) claim(role, "middle");
  }
  for (const [role, zone] of backRow) {
    if (role.startsWith("MB") && !spots.has(role)) {
      claim(role, zone === 1 ? "right" : "left", "left", "right");
    }
  }
  for (const [role] of backRow) {
    if (role === "L" && !spots.has(role)) claim(role, "left", "right");
  }
  for (const [role] of backRow) {
    if (!spots.has(role)) claim(role, "right", "left");
  }

  separate(spots, new Set());
  return spots;
}

/** Defensive base after our attack: three blockers and three deep defenders.
 * A back-row setter owns zone 1; the middle/libero covers 5 and outside covers 6.
 * Front-row setters block right, leaving the opposite to defend zone 1.
 * The outside in 6 stands deeper than 5 and 1: she takes the long balls, they
 * take the shorter ones. This is deliberately separate from the original attack
 * transition in switch.
 */
const DEFENCE_DEEP_OUTSIDE: Spot = { x: 0.2, z: 7.8 };
function defencePhase(slots: Slots, system: System): Spots {
  const spots = switchPhase(slots, system);
  for (const [role, zone] of slots) {
    if (isFrontRow(zone)) continue;
    if (role === system.setter || role === "OPP") spots.set(role, { x: 3.0, z: 6.6 });
    else if (role.startsWith("OH")) spots.set(role, { ...DEFENCE_DEEP_OUTSIDE });
    else spots.set(role, { x: -3.4, z: 6.6 });
  }
  return spots;
}

/* -------------------------------------------------------------- projection */

/**
 * Push a set of spots onto the legal side of Rule 7.4.3, then pull apart any
 * players who ended up inside each other, alternating until both hold.
 */
function enforceRules(spots: Spots, zoneByRole: Slots, anchored: Set<number>) {
  const byZone = new Map<number, Spot>();
  for (const [role, zone] of zoneByRole) byZone.set(zone, spots.get(role)!);

  for (let round = 0; round < 6; round += 1) {
    order(byZone, FRONT_BACK_PAIRS, "z", anchored);
    order(byZone, LATERAL_PAIRS, "x", anchored);
    if (!separate(spots, new Set())) break;
  }
  // A separation pass can nudge a player back across a line, so finish on the
  // rules: legality is the invariant that must hold, spacing is cosmetic.
  order(byZone, FRONT_BACK_PAIRS, "z", anchored);
  order(byZone, LATERAL_PAIRS, "x", anchored);
}

/**
 * Enforce `lower[key] + MARGIN <= upper[key]` for each pair, moving whichever
 * player is free to move — or splitting the difference when both are anchored.
 */
function order(byZone: Map<number, Spot>, pairs: number[][], key: keyof Spot, anchored: Set<number>) {
  for (let pass = 0; pass < 8; pass += 1) {
    let moved = false;

    for (const [lowZone, highZone] of pairs) {
      const low = byZone.get(lowZone);
      const high = byZone.get(highZone);
      if (!low || !high) continue;

      const deficit = low[key] + MARGIN - high[key];
      if (deficit <= 1e-6) continue;
      moved = true;

      const lowFixed = anchored.has(lowZone);
      const highFixed = anchored.has(highZone);
      if (lowFixed && !highFixed) high[key] += deficit;
      else if (highFixed && !lowFixed) low[key] -= deficit;
      else {
        low[key] -= deficit / 2;
        high[key] += deficit / 2;
      }
    }

    if (!moved) return;
  }
}

/** Pull apart any two players closer than MIN_GAP. Returns true if it moved anyone. */
function separate(spots: Spots, immovable: Set<string | null>) {
  const entries = [...spots].filter(([role]) => !immovable.has(role));
  let moved = false;

  for (let pass = 0; pass < 6; pass += 1) {
    let touched = false;

    for (let i = 0; i < entries.length; i += 1) {
      for (let j = i + 1; j < entries.length; j += 1) {
        const a = entries[i][1];
        const b = entries[j][1];
        let dx = b.x - a.x;
        let dz = b.z - a.z;
        let distance = Math.hypot(dx, dz);

        if (distance >= MIN_GAP) continue;
        if (distance < 1e-6) {
          dx = 0.1;
          dz = 0;
          distance = 0.1;
        }

        const push = (MIN_GAP - distance) / 2 + 0.01;
        const nx = (dx / distance) * push;
        const nz = (dz / distance) * push;
        a.x -= nx;
        a.z -= nz;
        b.x += nx;
        b.z += nz;
        touched = true;
        moved = true;
      }
    }

    if (!touched) break;
  }

  return moved;
}

/* ------------------------------------------------------------------ resolve */

/**
 * Resolve one rotation, serve state and phase into court positions.
 *
 * @returns `{ spots, byZone, roster, server, passers, legality, system, phase }`
 *          where `spots` is role -> `{ x, z }` in court metres.
 */
export function resolve({
  rotation,
  weServe,
  phase,
  system = SYSTEM_5_1,
  libero = true,
  attackZone,
}: FormationInput) {
  if (phase !== "defence" && !PHASES.includes(phase)) throw new Error(`unknown phase: ${phase}`);

  const roster = rosterFor({ rotation, weServe, libero });
  const slots = roster.slots;
  const server = weServe
    ? ([...slots].find(([, zone]) => zone === 1)?.[0] ?? null)
    : null;

  let spots;
  if (phase === "base") spots = basePhase(slots);
  else if (phase === "switch") {
    spots = switchPhase(slots, system);
    // R1 receiving: the front outside attacks right and opposite stays left.
    if (rotation === 1 && !weServe && system.id === "5-1") {
      spots.set("OH1", { x: 2.9, z: 1.2 });
      spots.set("OPP", { x: -2.9, z: 1.2 });
    }
  }
  else if (phase === "defence") {
    spots = defencePhase(slots, system);
    if(attackZone)spots=defencePlan(spots,slots,attackZone).spots;
  }
  else if (weServe) spots = servePhase(slots, system, server);
  else spots = receivePhase(slots, system, rotation);

  const byZone = new Map<number, Spot>();
  for (const [role, zone] of slots) byZone.set(zone, spots.get(role)!);

  return {
    spots,
    byZone,
    roster,
    server,
    passers: weServe ? [] : system.passersFor(slots),
    legality: weServe || phase === "defence" ? "tactical" : "ruleEnforced",
    system: system.id,
    phase,
    rotation,
    weServe,
  };
}
